// Local override of react_ujs/src/renderHelpers.js
// During the React 16 → 18 migration, React 18's hydrateRoot() is stricter
// about hydration mismatches than React 16's hydrate(). Pre-existing
// mismatches that React 16 silently patched now throw hard errors.
//
// This override passes onRecoverableError to hydrateRoot() so the app
// gracefully falls back to client rendering instead of showing an error overlay.
//
// It also wraps createRoot().render() in flushSync() so that DOM updates are
// committed synchronously, matching React 16's ReactDOM.render() behavior.
// This is required because react_ujs expects the DOM to be ready immediately
// after mountComponents() returns.

const ReactDOMClient = require('react-dom/client')

const ReactDOM = require('react-dom')

const { flushSync } = ReactDOM


// Track createRoot/hydrateRoot instances per DOM node so we can call
// root.unmount() later. WeakMap avoids memory leaks — when the DOM node
// is garbage collected, the root reference is too.
const rootsMap = new WeakMap()
const fallbackNodes = new WeakSet()
const hydrationErrorMessages = new WeakMap()

// A fallback re-renders the whole root from scratch on the main thread right after load, so
// it is an INP cost worth tracking per root; without a report it only shows as a console
// warning (two item page mismatches shipped unnoticed that way). Sampled to protect the
// Sentry quota: the mismatch is deterministic per page, so a sample still finds every case.
const HYDRATION_FALLBACK_SAMPLE_RATE = 0.1

function reportHydrationFallback(node, reason) {
  if (typeof window.sendErrorToSentry !== 'function' || Math.random() >= HYDRATION_FALLBACK_SAMPLE_RATE) {
    return
  }

  const componentName = node.getAttribute('data-react-class') || node.id || node.tagName
  const error = new Error(`Hydration fallback: ${componentName}`)
  error.name = 'HydrationFallback'

  // The stack is identical for every root (always these helper frames), so Sentry's default
  // grouping merged every component into one issue. Fingerprint per root instead.
  window.sendErrorToSentry(error, {
    fingerprint: ['hydration-fallback', componentName],
    tags: {
      component: componentName,
      page: window.location.pathname.split('/')[1] || 'root',
      viewport: window.innerWidth < 640 ? 'xs' : 'sm-and-up',
    },
    extra: { path: window.location.pathname, reason },
  })
}

export function supportsHydration() {
  return typeof ReactDOMClient.hydrateRoot === 'function' || typeof ReactDOM.hydrate === 'function'
}

// Fall back from failed hydration to a full client render.
// Clears the container's SSR markup and mounts from scratch so the page
// stays functional even when the server/client trees diverge.
function clientFallback(node, component, reason) {
  console.warn('[react-rails] Hydration failed – falling back to client render')
  reportHydrationFallback(node, reason)
  const existingRoot = rootsMap.get(node)
  if (existingRoot) {
    try { existingRoot.unmount() } catch (_e) { /* already broken */ }
    rootsMap.delete(node)
  }
  // Remove stale SSR children so createRoot starts fresh.
  while (node.firstChild) {
    node.removeChild(node.firstChild)
  }
  const freshRoot = ReactDOMClient.createRoot(node)
  rootsMap.set(node, freshRoot)
  flushSync(() => {
    freshRoot.render(component)
  })
}

// React 18 routes two kinds of errors through onRecoverableError: hydration
// failures (the SSR/client mismatch this override exists for) and render errors
// recovered during later concurrent updates. Only the former warrants rebuilding
// the tree — wiping the container for the latter would drop live component state.
// Production builds throw "Minified React error #NNN" instead of the full text;
// per React 18.3.1 scripts/error-codes/codes.json the hydration-recovery codes
// are 407, 418 and 421-425.
const MINIFIED_HYDRATION_ERROR = /Minified React error #(?:407|418|42[1-5])\b/
const DEV_HYDRATION_ERROR = /hydrat|server-rendered/i

function isHydrationError(message) {
  return DEV_HYDRATION_ERROR.test(message) || MINIFIED_HYDRATION_ERROR.test(message)
}

function scheduleClientFallback(node, component) {
  if (fallbackNodes.has(node)) {
    return
  }

  fallbackNodes.add(node)

  setTimeout(() => {
    fallbackNodes.delete(node)

    if (!node.isConnected) {
      rootsMap.delete(node)
      return
    }

    clientFallback(node, component, hydrationErrorMessages.get(node))
  }, 0)
}

export function reactHydrate(node, component) {
  if (typeof ReactDOMClient.hydrateRoot === 'function') {
    // react_ujs may mount the same SSR node again later in the page lifecycle.
    // Reuse the existing root instead of calling hydrateRoot twice.
    const existingRoot = rootsMap.get(node)

    // A second mount pass over an already-rooted SSR node must be a no-op. The props
    // come from the same unchanged data-react-props attribute, so re-rendering buys
    // nothing, and if the first pass is still hydrating, render() lands as an "early
    // update", which makes React discard the SSR tree and take the clientFallback path
    // that empties the container.
    if (existingRoot) {
      return existingRoot
    }

    try {
      const root = ReactDOMClient.hydrateRoot(node, component, {
        onRecoverableError(error) {
          const message = (error && error.message) || String(error)

          if (process.env.NODE_ENV !== 'production') {
            console.warn('[react-rails] Recoverable render error:', message)
          }

          if (isHydrationError(message)) {
            if (!hydrationErrorMessages.has(node)) {
              hydrationErrorMessages.set(node, message)
            }
            scheduleClientFallback(node, component)
          }
        },
      })
      rootsMap.set(node, root)
      return root
    } catch (error) {
      // hydrateRoot itself can throw synchronously on severe mismatches.
      console.error('[react-rails] hydrateRoot threw:', error.message)
      clientFallback(node, component, error.message)
      return rootsMap.get(node)
    }
  }

  return ReactDOM.hydrate(component, node)
}

export function createReactRootLike(node) {
  if (ReactDOMClient.createRoot) {
    // react_ujs may call mountComponents() more than once on the same node.
    // Reuse the existing root to avoid duplicate mounts and side-effects.
    const existingRoot = rootsMap.get(node)
    if (existingRoot) {
      return {
        render(component) {
          flushSync(() => {
            existingRoot.render(component)
          })
        },
      }
    }

    const root = ReactDOMClient.createRoot(node)
    rootsMap.set(node, root)
    return {
      render(component) {
        flushSync(() => {
          root.render(component)
        })
      },
    }
  }

  return legacyReactRootLike(node)
}

function legacyReactRootLike(node) {
  return {
    render(component) {
      ReactDOM.render(component, node)
    },
  }
}

export function unmountReactRoot(node) {
  const root = rootsMap.get(node)
  if (root) {
    root.unmount()
    rootsMap.delete(node)
    return true
  }
  return false
}
